import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import { ticketStatusesApi } from '../../api/ticketStatuses';
import { useCompany } from '../../contexts/CompanyContext';
import EmptyState from '../EmptyState';
import type { TicketStatusDef, StatusCategory } from '../../types';

// Semantic categories — these drive built-in behavior no matter what the
// status is called.
const CATEGORY_OPTIONS: { value: StatusCategory; label: string; hint: string }[] = [
  { value: 'new',              label: 'New',                hint: 'Fresh, untouched tickets' },
  { value: 'open',             label: 'Open / in progress', hint: 'Counts as active work' },
  { value: 'waiting_customer', label: 'Waiting on customer', hint: 'Customer reply reopens the ticket' },
  { value: 'waiting_internal', label: 'Waiting internal',   hint: 'Paused on your side' },
  { value: 'resolved',         label: 'Resolved',           hint: 'Requires a resolution entry' },
  { value: 'closed',           label: 'Closed',             hint: 'Requires a close reason if unresolved' },
];

const TONE_OPTIONS = [
  { value: 'primary', label: 'Blue (brand)' },
  { value: 'ok',      label: 'Green' },
  { value: 'warning', label: 'Amber' },
  { value: 'error',   label: 'Red' },
  { value: 'muted',   label: 'Gray' },
];

const toneCss = (color: string) =>
  color.startsWith('#')
    ? color
    : `var(--scpa-${color === 'primary' ? 'primary' : color === 'muted' ? 'text-muted' : `status-${color}`})`;

interface FormState {
  label: string;
  category: StatusCategory;
  color: string;
  notifyCustomer: boolean;
  isDefault: boolean;
}

const EMPTY_FORM: FormState = { label: '', category: 'open', color: 'primary', notifyCustomer: false, isDefault: false };

export default function TicketStatusesTab() {
  const queryClient = useQueryClient();
  const { currentCompany } = useCompany();
  const companyId = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const { data: statuses = [], isLoading } = useQuery({
    queryKey: ['ticket-statuses', companyId],
    queryFn: () => ticketStatusesApi.list(),
    enabled: !!currentCompany,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['ticket-statuses'] });

  const createMutation = useMutation({
    mutationFn: () => ticketStatusesApi.create(form),
    onSuccess: () => { invalidate(); setAdding(false); setForm(EMPTY_FORM); toast.success('Status created'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e.message),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; data: Partial<TicketStatusDef> }) =>
      ticketStatusesApi.update(vars.id, vars.data),
    onSuccess: () => { invalidate(); setEditingId(null); toast.success('Status updated'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ticketStatusesApi.delete(id),
    onSuccess: () => { invalidate(); toast.success('Status deleted'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e.message),
  });

  const startEdit = (s: TicketStatusDef) => {
    setAdding(false);
    setEditingId(s.id);
    setForm({ label: s.label, category: s.category, color: s.color, notifyCustomer: s.notifyCustomer, isDefault: s.isDefault });
  };

  const saveEdit = (s: TicketStatusDef) => {
    if (!form.label.trim()) return toast.error('Label is required');
    updateMutation.mutate({ id: s.id, data: form });
  };

  const move = (s: TicketStatusDef, dir: -1 | 1) => {
    const sorted = [...statuses].sort((a, b) => a.sortOrder - b.sortOrder);
    const idx = sorted.findIndex(x => x.id === s.id);
    const swap = sorted[idx + dir];
    if (!swap) return;
    updateMutation.mutate({ id: s.id, data: { sortOrder: swap.sortOrder } });
    updateMutation.mutate({ id: swap.id, data: { sortOrder: s.sortOrder } });
  };

  const categoryLabel = (c: StatusCategory) => CATEGORY_OPTIONS.find(o => o.value === c)?.label ?? c;

  const formFields = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className="label">Label</label>
        <input
          className="input w-full"
          value={form.label}
          onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
          placeholder="e.g. On Hold, In Review, Won't Fix…"
          autoFocus
        />
      </div>
      <div>
        <label className="label">Meaning (category)</label>
        <select
          className="select w-full"
          value={form.category}
          onChange={e => setForm(f => ({ ...f, category: e.target.value as StatusCategory }))}
        >
          {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <p className="text-[11px] text-muted mt-1">
          {CATEGORY_OPTIONS.find(o => o.value === form.category)?.hint}
        </p>
      </div>
      <div>
        <label className="label">Color</label>
        <select
          className="select w-full"
          value={form.color}
          onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
        >
          {TONE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          {form.color.startsWith('#') && <option value={form.color}>{form.color}</option>}
        </select>
      </div>
      <div className="flex flex-col justify-end gap-1.5 pb-1">
        <label className="flex items-center gap-2 text-[13px] text-fg cursor-pointer select-none">
          <input
            type="checkbox"
            className="rounded"
            checked={form.notifyCustomer}
            onChange={e => setForm(f => ({ ...f, notifyCustomer: e.target.checked }))}
          />
          Email customer when a ticket enters this status
        </label>
        <label className="flex items-center gap-2 text-[13px] text-fg cursor-pointer select-none">
          <input
            type="checkbox"
            className="rounded"
            checked={form.isDefault}
            onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))}
          />
          Default status for new tickets
        </label>
      </div>
    </div>
  );

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="section-title">Ticket Statuses</h2>
        {!adding && (
          <button
            className="btn btn-primary inline-flex items-center gap-2"
            onClick={() => { setEditingId(null); setForm(EMPTY_FORM); setAdding(true); }}
          >
            <Plus className="w-4 h-4" />Add
          </button>
        )}
      </div>
      <p className="text-sm text-muted mb-4">
        Rename, recolor, add or retire the statuses your tickets move through.
        Each status has a <em>meaning</em> so the app still knows what counts
        as open, waiting, resolved or closed.
      </p>

      {adding && (
        <div className="border border-line rounded-lg p-4 mb-4 space-y-3">
          {formFields}
          <div className="flex gap-2">
            <button
              className="btn btn-primary"
              onClick={() => form.label.trim() ? createMutation.mutate() : toast.error('Label is required')}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating…' : 'Create status'}
            </button>
            <button className="btn btn-secondary" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="py-8 text-center text-muted text-sm">Loading…</div>
      ) : statuses.length === 0 ? (
        <EmptyState icon={<Plus className="w-6 h-6" />}>
          No statuses yet — defaults are created automatically on first use.
        </EmptyState>
      ) : (
        <div className="space-y-1">
          {statuses.map((s, i) => (
            <div key={s.id} className="border-b border-line-light">
              <div className={`flex items-center gap-3 py-2.5 ${!s.isActive ? 'opacity-50' : ''}`}>
                {/* Reorder */}
                <div className="flex flex-col -my-1">
                  <button
                    className="text-muted hover:text-fg text-[10px] leading-none px-1 disabled:opacity-30"
                    onClick={() => move(s, -1)}
                    disabled={i === 0 || updateMutation.isPending}
                    title="Move up"
                  >▲</button>
                  <button
                    className="text-muted hover:text-fg text-[10px] leading-none px-1 disabled:opacity-30"
                    onClick={() => move(s, 1)}
                    disabled={i === statuses.length - 1 || updateMutation.isPending}
                    title="Move down"
                  >▼</button>
                </div>

                <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: toneCss(s.color) }} />
                <span className="font-medium text-fg">{s.label}</span>
                <span className="text-xs text-muted">{categoryLabel(s.category)}</span>
                {s.isDefault && <span className="badge">default</span>}
                {!s.isActive && <span className="badge">inactive</span>}
                {s.notifyCustomer && <span className="text-[11px] text-muted">✉ customer</span>}
                <span className="text-xs text-muted ml-auto shrink-0">
                  {s.ticketCount ?? 0} ticket{(s.ticketCount ?? 0) === 1 ? '' : 's'}
                </span>

                <button
                  className="p-1.5 rounded text-muted hover:text-fg hover:bg-row-odd"
                  title="Edit"
                  onClick={() => (editingId === s.id ? setEditingId(null) : startEdit(s))}
                >
                  {editingId === s.id ? <X className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                </button>
                <button
                  className="p-1.5 rounded text-muted hover:text-fg hover:bg-row-odd"
                  title={s.isActive ? 'Deactivate (keeps history, hides from pickers)' : 'Reactivate'}
                  onClick={() => updateMutation.mutate({ id: s.id, data: { isActive: !s.isActive } })}
                  disabled={updateMutation.isPending}
                >
                  {s.isActive ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                </button>
                <button
                  className="p-1.5 rounded text-muted hover:text-err hover:bg-row-odd disabled:opacity-30"
                  title={(s.ticketCount ?? 0) > 0 ? 'In use — deactivate instead' : 'Delete'}
                  onClick={() => deleteMutation.mutate(s.id)}
                  disabled={deleteMutation.isPending || (s.ticketCount ?? 0) > 0 || s.isDefault}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {editingId === s.id && (
                <div className="border border-line rounded-lg p-4 mb-3 space-y-3">
                  {formFields}
                  <div className="flex gap-2">
                    <button
                      className="btn btn-primary"
                      onClick={() => saveEdit(s)}
                      disabled={updateMutation.isPending}
                    >
                      {updateMutation.isPending ? 'Saving…' : 'Save'}
                    </button>
                    <button className="btn btn-secondary" onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-muted mt-4">
        Statuses in use can't be deleted — deactivate them instead; existing
        tickets keep the status but it disappears from pickers. Changing a
        status's meaning re-categorizes every ticket currently in it.
      </p>
    </div>
  );
}
